import { WorkflowState } from '@compass/client/types/case_record';
import type { Evaluation } from '@compass/client/types/evaluation';
import { CircleOff, Loader2 } from 'lucide-react';
import React from 'react';

import { cn } from '@/utils';

export const getEvaluationWorkflowBannerState = (evaluation: Evaluation) => {
  const hasReviewerFindingReviews = Object.keys(evaluation.reviews?.findings || {}).length > 0;
  const isWorkflowProcessing = evaluation.workflow_state === WorkflowState.WORKFLOW_STATE_RUNNING;
  const hasStaleDerivedValues =
    hasReviewerFindingReviews &&
    (evaluation.workflow_state === WorkflowState.WORKFLOW_STATE_STOPPED ||
      evaluation.workflow_state === WorkflowState.WORKFLOW_STATE_CANCELLED);

  return {
    isWorkflowProcessing,
    hasStaleDerivedValues,
    shouldShowWorkflowBanner: isWorkflowProcessing || hasStaleDerivedValues,
  };
};

interface QcWorkflowStatusBannerProps {
  evaluation: Evaluation;
  className?: string;
}

export const QcWorkflowStatusBanner: React.FC<QcWorkflowStatusBannerProps> = ({
  evaluation,
  className,
}) => {
  const { isWorkflowProcessing, hasStaleDerivedValues } =
    getEvaluationWorkflowBannerState(evaluation);

  if (!isWorkflowProcessing && !hasStaleDerivedValues) {
    return null;
  }

  return (
    <div
      className={cn(
        'sticky top-0 z-20 px-4 py-3 shadow-sm',
        isWorkflowProcessing ? 'bg-notice' : 'bg-critical',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-start gap-3',
          isWorkflowProcessing ? 'text-amber-900' : 'text-critical-foreground',
        )}
      >
        {isWorkflowProcessing ? (
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
        ) : (
          <CircleOff className="mt-0.5 h-4 w-4 shrink-0" />
        )}
        <div className="min-w-0">
          <div className="text-sm font-semibold uppercase tracking-wide">
            {isWorkflowProcessing ? 'Update in progress' : 'Update needs attention'}
          </div>
          <div className={cn('text-sm', isWorkflowProcessing && 'text-amber-800')}>
            {isWorkflowProcessing
              ? 'Recomputing section scores, overall score, and call-level outcomes.'
              : 'Reviewer answers were saved, but recomputation did not complete. Displayed values may be stale.'}
          </div>
        </div>
      </div>
    </div>
  );
};
